import { useUnit } from 'effector-react';
import { Button, Card, Space, Switch, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { CheckOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import style from './MapGradient.module.css';
import {
  $activeFilters,
  changeActiveFilterEv,
} from '../../models/activeFiltersModel/index.js';
import {
  $blockedBuckets,
  $gradientBuckets,
} from '../../models/gradientModel/index.js';
import { $mapLoaded } from '../../models/mapModel/index.js';
import { formatNumberCompact } from '../../utils/format-utils.js';
import {
  $hexProp,
  $priceType,
  togglePriceTypeEv,
} from '../../models/centerHomeModel/index.js';
import { ch_title_dict } from '../../data/ch_title_dict.js';
import useWindowSize from '../../hooks/useWindowSize.js';

export default function MapGradient() {
  const mapLoaded = useUnit($mapLoaded);
  const gradientBuckets = useUnit($gradientBuckets);
  const activeGradientFilter = useUnit($activeFilters).gradient;
  const activeFilters = useUnit($activeFilters);
  const blockedBuckets = useUnit($blockedBuckets);
  const { pathname } = useLocation();
  const hideNumbers =
    activeFilters.chosen_metrics[0] === 'income_avg_range' ||
    activeFilters.chosen_metrics[0] === 'rent_price_range';
  const [blockLevel, setBlockLevel] = useState('zoom_8');
  const bucketsCopy = gradientBuckets.buckets;
  const changeActiveFilter = useUnit(changeActiveFilterEv);
  const hexProp = useUnit($hexProp);
  const priceType = useUnit($priceType);
  const togglePriceType = useUnit(togglePriceTypeEv);
  const [width, height] = useWindowSize();
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (window.map) {
      if (mapLoaded) {
        window.map.on('render', () => {
          const zoom = window.map.getZoom();
          let level;
          if (zoom < 11) {
            level = 'zoom_8';
          } else if (zoom >= 11 && zoom < 13) {
            level = 'zoom_9';
          } else {
            level = 'zoom_10';
          }
          if (blockLevel !== level) {
            setBlockLevel(level);
          }
        });
      }
    }
  }, [mapLoaded]);

  let title;
  if (pathname === '/center-home') {
    title =
      ch_title_dict[`${hexProp}${priceType === 'absolute' ? '_full' : ''}`];
  }

  if (width < 500 && !expanded) {
    return (
      <div
        className={style.bottom_bar_wrapper}
        style={{ justifyContent: 'center' }}
      >
        <Card className={style.gradient_wrapper}>
          Условные обозначения
          <Button
            className={style.mobile_expand_button}
            shape="circle"
            icon={<UpOutlined />}
            onClick={() => setExpanded(true)}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className={style.bottom_bar_wrapper}>
      <Card
        className={style.gradient_wrapper}
        bodyStyle={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {pathname === '/center-home' && width < 500 ? (
          <Button
            className={style.mobile_close_button}
            shape="circle"
            icon={<DownOutlined />}
            onClick={() => setExpanded(false)}
          />
        ) : null}
        <div>
          <div
            className={style.gradient_top}
            style={{
              justifyContent:
                pathname !== '/public' || pathname === '/lite'
                  ? 'flex-start'
                  : 'center',
            }}
          >
            <div className={style.gradient_by_wrapper}>
              <Tooltip title={title} placement="top">
                <div
                  style={{
                    color: 'black',
                  }}
                  className={style.gradient_title}
                >
                  {title}
                </div>
              </Tooltip>
              <div className={style.chh_switch}>
                <Switch
                  size="small"
                  checked={priceType === 'absolute'}
                  onChange={togglePriceType}
                />{' '}
                {priceType === 'absolute'
                  ? 'Показать стоимость кв метра'
                  : 'Показать стоимость квартир'}
              </div>
            </div>
          </div>
          <Space
            size="small"
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: width < 500 ? 'wrap' : 'nowrap',
              rowGap: 20,
            }}
          >
            {hideNumbers && (
              <div className={style.bucket_wrapper}>
                <div className={style.bucket} style={{ color: 'black' }}>
                  Низкий
                </div>
              </div>
            )}
            {bucketsCopy.map((item, index) => {
              if (index !== bucketsCopy.length - 1) {
                return (
                  <>
                    {!hideNumbers && (
                      <div className={style.bucket_wrapper}>
                        <div
                          className={style.bucket}
                          style={{ color: 'black' }}
                        >
                          {formatNumberCompact(
                            item,
                            activeFilters.chosen_metrics[0]
                          )}
                        </div>
                      </div>
                    )}
                    <GradientItem
                      key={`${item}${index}`}
                      maxValue={bucketsCopy[index + 1] || 0}
                      minValue={item}
                      color={gradientBuckets.colors[index]}
                      changeActiveFilter={changeActiveFilter}
                      activeGradientFilter={activeGradientFilter}
                      blockedBuckets={blockedBuckets[blockLevel]}
                      isLast={index === bucketsCopy.length - 2}
                    />
                    {index === bucketsCopy.length - 2 && !hideNumbers && (
                      <div className={style.bucket_wrapper}>
                        <div
                          className={style.bucket}
                          style={{ color: 'black' }}
                        >
                          {bucketsCopy[index + 1] === Infinity
                            ? '∞'
                            : formatNumberCompact(
                                bucketsCopy[index + 1],
                                activeFilters.chosen_metrics[0]
                              )}
                        </div>
                      </div>
                    )}
                  </>
                );
              }
            })}
            {hideNumbers && (
              <div className={style.bucket_wrapper}>
                <div className={style.bucket} style={{ color: 'black' }}>
                  Высокий
                </div>
              </div>
            )}
          </Space>
        </div>
      </Card>
    </div>
  );
}

function GradientItem({ color, maxValue, minValue, activeGradientFilter }) {
  return (
    <div
      className={[
        style.gradient_item,
        activeGradientFilter.some(
          (item) => item.min === minValue && item.max === maxValue
        )
          ? style.active
          : '',
      ].join(' ')}
      style={{
        background: color,
        cursor: 'default',
        boxShadow: 'none',
      }}
    >
      <div
        className={style.badge}
        style={{
          opacity: activeGradientFilter.some(
            (item) => item.min === minValue && item.max === maxValue
          )
            ? 1
            : 0,
        }}
      >
        <CheckOutlined style={{ fontSize: '13px' }} />
      </div>
    </div>
  );
}
